const flickityOpts = {
  cellAlign: "left",
  contain: true,
  adaptiveHeight: true,
  pageDots: false,
  prevNextButtons: false,
  wrapAround: true,
  draggable: true,
};

const flickityOptsFor = () => ({
  ...flickityOpts,
  cellAlign: "left",
  contain: !isMobile(),
  adaptiveHeight: !isMobile(),
});

const NAV_SPACING = { 0: 0, 1: 45, 2: 81, 3: 113, 4: 142 };

const navStyleForOffset = (abs) => {
  if (abs === 0) return { fontSize: 40, opacity: 1 };
  if (abs === 1) return { fontSize: 22, opacity: 0.9 };
  if (abs === 2) return { fontSize: 14, opacity: 0.7 };
  if (abs === 3) return { fontSize: 12, opacity: 0.5 };
  if (abs === 4) return { fontSize: 8, opacity: 0.3 };
  return { fontSize: 10, opacity: 0.2 };
};

const isMobile = () => window.matchMedia("(max-width: 768px)").matches;

const prefersReducedMotion = () =>
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const canScrollCarousel = (flkty) =>
  flkty.cells.length > 1 &&
  flkty.slideableWidth > flkty.size.innerWidth + 2;

const syncCarouselScrollState = (root, flkty) => {
  if (!canScrollCarousel(flkty)) {
    root.classList.remove("is-carousel-scrollable", "is-carousel-at-end");
    return;
  }

  root.classList.add("is-carousel-scrollable");
  const atEnd =
    Math.abs(flkty.x) >= flkty.slideableWidth - flkty.size.innerWidth - 4;
  root.classList.toggle("is-carousel-at-end", atEnd);
};

const PEEK_SCROLL_FRACTION = 0.4;
const PEEK_DURATION_MS = 1600;
const PEEK_PAUSE_MS = 300;

const getPeekDistance = (flkty) => {
  const cell = flkty.cells[flkty.selectedIndex] ?? flkty.cells[0];
  if (!cell) return 80;
  const width = cell.size?.outerWidth ?? cell.element.offsetWidth;
  return Math.round(width * PEEK_SCROLL_FRACTION);
};

const isSectionCentered = (section) => {
  const rect = section.getBoundingClientRect();
  if (rect.bottom <= 0 || rect.top >= window.innerHeight) return false;
  const sectionCenter = rect.top + rect.height / 2;
  const viewportCenter = window.innerHeight / 2;
  return Math.abs(sectionCenter - viewportCenter) <= window.innerHeight * 0.12;
};

const updateSliderPosition = (flkty) => {
  if (typeof flkty.positionSlider === "function") {
    flkty.positionSlider();
    return;
  }
  flkty.slider.style.transform = `translate3d(${flkty.x}px, 0, 0)`;
};

const animateCarouselX = (flkty, targetX, duration) =>
  new Promise((resolve) => {
    const startX = flkty.x;
    const startTime = performance.now();
    const ease = (t) =>
      t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2;

    const step = (now) => {
      const t = Math.min(1, (now - startTime) / duration);
      flkty.x = startX + (targetX - startX) * ease(t);
      updateSliderPosition(flkty);
      if (t < 1) requestAnimationFrame(step);
      else {
        flkty.x = targetX;
        updateSliderPosition(flkty);
        resolve();
      }
    };

    requestAnimationFrame(step);
  });

const runPeekAnimation = async (root, flkty) => {
  if (root.dataset.fcsScrollHintDone === "true") return;
  if (!flkty || prefersReducedMotion()) return;
  if (!canScrollCarousel(flkty)) return;

  root.dataset.fcsScrollHintDone = "true";
  root.classList.add("is-scroll-hint");

  const startX = flkty.x;
  const peekX = startX - getPeekDistance(flkty);
  const wasDraggable = flkty.options.draggable;
  flkty.options.draggable = false;

  try {
    await animateCarouselX(flkty, peekX, PEEK_DURATION_MS);
    await new Promise((r) => window.setTimeout(r, PEEK_PAUSE_MS));
    await animateCarouselX(flkty, startX, PEEK_DURATION_MS);
  } finally {
    flkty.options.draggable = wasDraggable;
    root.classList.remove("is-scroll-hint");
    root.classList.add("is-scroll-hint-complete");
    syncCarouselScrollState(root, flkty);
  }
};

const bindCarouselScrollState = (root, flkty, carousel) => {
  if (!flkty) return;

  const dismissHint = () => {
    root.dataset.fcsScrollHintDone = "true";
    root.classList.remove("is-scroll-hint");
    root.classList.add("is-scroll-hint-complete");
  };

  carousel.addEventListener("pointerdown", dismissHint, { once: true });
  root.querySelector("[data-fcs-next]")?.addEventListener("click", dismissHint, {
    once: true,
  });

  flkty.on("scroll", () => syncCarouselScrollState(root, flkty));
  flkty.on("select", () => syncCarouselScrollState(root, flkty));
  flkty.on("settle", () => syncCarouselScrollState(root, flkty));
  syncCarouselScrollState(root, flkty);
};

const bindSectionPeek = (root) => {
  if (root.dataset.fcsPeekBound === "true" || prefersReducedMotion()) return;
  root.dataset.fcsPeekBound = "true";

  const section = root.closest(".shopify-section--featured-collections-showcase");
  if (!section) return;

  let peekStarted = false;

  const tryRunPeek = () => {
    const flkty = root.fcsGetActiveFlkty?.();
    if (!flkty) return false;
    flkty.resize();
    if (!canScrollCarousel(flkty)) {
      syncCarouselScrollState(root, flkty);
      return true;
    }
    runPeekAnimation(root, flkty);
    return true;
  };

  const cleanup = () => {
    window.removeEventListener("scroll", onScroll);
    window.removeEventListener("resize", onScroll);
    observer.disconnect();
  };

  const runWhenCentered = () => {
    if (peekStarted || root.dataset.fcsScrollHintDone === "true") return;
    if (!isSectionCentered(section)) return;

    peekStarted = true;
    cleanup();

    let attempts = 0;
    const attempt = () => {
      if (tryRunPeek() || attempts >= 24) return;
      attempts += 1;
      window.setTimeout(attempt, 150);
    };

    attempt();
  };

  const onScroll = () => runWhenCentered();

  const observer = new IntersectionObserver(
    () => runWhenCentered(),
    { threshold: 0.15 },
  );

  observer.observe(section);
  window.addEventListener("scroll", onScroll, { passive: true });
  window.addEventListener("resize", onScroll, { passive: true });
  requestAnimationFrame(runWhenCentered);
};

const layoutU = (root) => {
  const artboard = isMobile() ? 390 : 1440;
  return root.getBoundingClientRect().width / artboard;
};

const typeU = (root) => {
  const wideScale =
    parseFloat(getComputedStyle(root).getPropertyValue("--hueb-wide-scale")) ||
    1;
  return layoutU(root) * wideScale;
};

const syncViewportHeight = (flkty) => {
  flkty.cells.forEach(({ element }) => {
    element.style.height = "";
    element.querySelector(".fcs__card")?.style.removeProperty("min-height");
  });

  let maxHeight = 0;
  flkty.cells.forEach(({ element }) => {
    const height = Math.ceil(element.getBoundingClientRect().height);
    if (height > maxHeight) maxHeight = height;
  });
  if (maxHeight === 0) return;

  if (isMobile()) {
    flkty.viewport.style.height = `${maxHeight}px`;
    return;
  }

  flkty.cells.forEach(({ element }) => {
    const card = element.querySelector(".fcs__card");
    if (card) card.style.minHeight = `${maxHeight}px`;
  });
  flkty.viewport.style.height = `${maxHeight}px`;
};

const refresh = (flkty, { syncHeight = true } = {}) => {
  const mobile = isMobile();
  flkty.options.cellAlign = "left";
  flkty.options.contain = !mobile;
  flkty.options.adaptiveHeight = !mobile;
  flkty.resize();
  if (!syncHeight) return;
  requestAnimationFrame(() => {
    syncViewportHeight(flkty);
    flkty.reposition();
  });
};

const bindResize = (root, flkty, carousel) => {
  let frame;
  const schedule = (syncHeight = true) => {
    cancelAnimationFrame(frame);
    frame = requestAnimationFrame(() => refresh(flkty, { syncHeight }));
  };

  if (typeof ResizeObserver !== "undefined" && !isMobile()) {
    new ResizeObserver(() => schedule()).observe(root);
  }

  window.addEventListener("resize", () => schedule(), { passive: true });
  carousel?.querySelectorAll("img").forEach((img) => {
    if (!img.complete) img.addEventListener("load", () => schedule(), { once: true });
  });
};

const bindStageControls = (root) => {
  const controls = root.querySelector("[data-fcs-controls]");
  if (!controls || controls.dataset.fcsControlsBound) return;

  const next = controls.querySelector("[data-fcs-next]");
  let activeFlkty = null;

  const syncControls = () => {
    if (next) next.disabled = !activeFlkty;
  };

  next?.addEventListener("click", () => activeFlkty?.next());

  controls.dataset.fcsControlsBound = "true";
  root.fcsSetActiveCarousel = (flkty) => {
    activeFlkty = flkty;
    syncControls();
    if (!flkty) return;
    flkty.on("select", syncControls);
    flkty.on("ready", syncControls);
  };

  root.fcsGetActiveFlkty = () => activeFlkty;
};

const initCarousel = (panel, root) => {
  const carousel = panel.querySelector("[data-fcs-carousel]");
  if (!carousel || typeof Flickity === "undefined") {
    root.fcsSetActiveCarousel?.(null);
    return null;
  }

  let flkty = Flickity.data(carousel);
  if (!flkty) {
    flkty = new Flickity(carousel, flickityOptsFor());
    bindResize(root, flkty, carousel);
    if (!isMobile()) {
      flkty.on("settle", () => refresh(flkty, { syncHeight: true }));
    }
  } else {
    refresh(flkty);
  }

  bindCarouselScrollState(root, flkty, carousel);

  root.fcsSetActiveCarousel?.(flkty);

  if (isMobile()) {
    let syncFrame;
    const syncStable = () => {
      cancelAnimationFrame(syncFrame);
      syncFrame = requestAnimationFrame(() => refresh(flkty));
    };
    flkty.on("ready", syncStable);
    carousel.querySelectorAll("img").forEach((img) => {
      if (!img.complete) img.addEventListener("load", syncStable, { once: true });
    });
    syncStable();
  } else {
    flkty.on("ready", () => {
      carousel.querySelectorAll("img").forEach((img) => {
        if (img.complete) refresh(flkty);
        else img.addEventListener("load", () => refresh(flkty), { once: true });
      });
      refresh(flkty);
    });
    requestAnimationFrame(() => refresh(flkty));
  }

  return flkty;
};

const syncMobileNavIndicator = (navList, indicator, activeTab) => {
  if (!navList || !indicator || !activeTab) return;
  indicator.style.width = `${activeTab.offsetWidth}px`;
  indicator.style.transform = `translate3d(${activeTab.offsetLeft - navList.scrollLeft}px, 0, 0)`;
};

const updateMobileNav = (
  root,
  activeIndex,
  { scrollTabIntoView = false } = {},
) => {
  const navList = root.querySelector("[data-fcs-nav]");
  if (!navList) return;

  const indicator = root.querySelector("[data-fcs-nav-indicator]");
  const items = [...navList.querySelectorAll("[data-fcs-tab]")];

  items.forEach((tab, i) => {
    const on = i === activeIndex;
    tab.classList.toggle("is-active", on);
    tab.setAttribute("aria-current", on ? "true" : "false");
    tab.style.top = "";
    tab.style.fontSize = "";
    tab.style.opacity = "";
  });

  const active = items[activeIndex];
  if (!active) return;

  const sync = () => syncMobileNavIndicator(navList, indicator, active);

  requestAnimationFrame(() => {
    sync();
    if (scrollTabIntoView) {
      navList.scrollTo({
        left:
          active.offsetLeft - (navList.clientWidth - active.offsetWidth) / 2,
        behavior: "smooth",
      });
      requestAnimationFrame(sync);
    }
  });
};

const updateNavPositions = (root, activeIndex) => {
  const navList = root.querySelector("[data-fcs-nav]");
  if (!navList) return;

  root.dataset.fcsActiveIndex = String(activeIndex);

  if (isMobile()) {
    const scrollTab = root.dataset.fcsNavScrollTab === String(activeIndex);
    updateMobileNav(root, activeIndex, { scrollTabIntoView: scrollTab });
    delete root.dataset.fcsNavScrollTab;
    return;
  }

  const indicator = root.querySelector("[data-fcs-nav-indicator]");
  if (indicator) {
    indicator.style.width = "";
    indicator.style.transform = "";
  }

  const items = [...navList.querySelectorAll("[data-fcs-tab]")];

  items.forEach((tab) => {
    tab.style.width = "";
    tab.style.minWidth = "";
    tab.style.flex = "";
  });

  const centerY = navList.offsetHeight / 2;
  const u = layoutU(root);
  const typeScale = typeU(root);

  items.forEach((tab, i) => {
    let offset = (i - activeIndex + items.length) % items.length;
    if (offset > items.length / 2) offset -= items.length;

    const abs = Math.abs(offset);
    const { fontSize, opacity } = navStyleForOffset(abs);
    const space = (NAV_SPACING[abs] ?? 142) * u;
    const y = centerY + (offset < 0 ? -space : space);

    tab.style.top = `${y}px`;
    tab.style.fontSize = `${fontSize * typeScale}px`;
    tab.style.opacity = String(opacity);
    tab.classList.toggle("is-active", offset === 0);
    tab.setAttribute("aria-current", offset === 0 ? "true" : "false");
  });
};

const activatePanel = (root, index) => {
  const panels = [...root.querySelectorAll("[data-fcs-panel]")];

  panels.forEach((panel, i) => {
    const on = i === index;
    panel.classList.toggle("is-active", on);
    panel.hidden = !on;
    if (on) initCarousel(panel, root);
  });

  updateNavPositions(root, index);
};

const init = (scope = document) => {
  scope.querySelectorAll("[data-fcs]").forEach((root) => {
    if (root.dataset.fcsReady) return;
    root.dataset.fcsReady = "true";

    const tabs = [...root.querySelectorAll("[data-fcs-tab]")];
    if (!tabs.length) return;

    let activeIndex = tabs.findIndex((t) => t.classList.contains("is-active"));
    if (activeIndex < 0) activeIndex = 0;

    if (!(window.Shopify && window.Shopify.designMode) && tabs.length > 1) {
      activeIndex = Math.floor(Math.random() * tabs.length);
    }

    const setActive = (index) => {
      activeIndex = index;
      root.dataset.fcsNavScrollTab = String(index);
      activatePanel(root, index);
    };

    tabs.forEach((tab) => {
      tab.addEventListener("click", () => {
        const i = Number(tab.dataset.fcsIndex);
        if (!Number.isNaN(i)) setActive(i);
      });
      tab.addEventListener("keydown", (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          const i = Number(tab.dataset.fcsIndex);
          if (!Number.isNaN(i)) setActive(i);
        }
      });
    });

    let resizeFrame;
    window.addEventListener(
      "resize",
      () => {
        cancelAnimationFrame(resizeFrame);
        resizeFrame = requestAnimationFrame(() =>
          updateNavPositions(root, activeIndex),
        );
      },
      { passive: true },
    );

    const navList = root.querySelector("[data-fcs-nav]");
    if (navList && !navList.dataset.fcsNavScrollBound) {
      navList.dataset.fcsNavScrollBound = "true";
      navList.addEventListener(
        "scroll",
        () => {
          const i = Number(root.dataset.fcsActiveIndex);
          if (Number.isNaN(i)) return;
          const indicator = root.querySelector("[data-fcs-nav-indicator]");
          const active = navList.querySelectorAll("[data-fcs-tab]")[i];
          syncMobileNavIndicator(navList, indicator, active);
        },
        { passive: true },
      );
    }

    bindStageControls(root);
    bindSectionPeek(root);
    setActive(activeIndex);
  });
};

document.readyState === "loading"
  ? document.addEventListener("DOMContentLoaded", () => init(), { once: true })
  : init();

document.addEventListener("shopify:section:load", (e) => {
  e.target.querySelectorAll("[data-fcs]").forEach((root) => {
    delete root.dataset.fcsReady;
    delete root.dataset.fcsPeekBound;
    delete root.dataset.fcsScrollHintDone;
    root.classList.remove(
      "is-scroll-hint",
      "is-scroll-hint-complete",
      "is-carousel-scrollable",
      "is-carousel-at-end",
    );
  });
  init(e.target);
});
